import {
  TextField,
  Button,
  Step,
  StepLabel,
  Stepper,
  Box,
  FormControl,
  NativeSelect,
  OutlinedInput,
  Typography,
} from "@mui/material";
import React, { Dispatch, SetStateAction, useEffect } from "react";
import { useAppSelector } from "../../../store/reduxHooks";
import {
  ICreateUpdateRole,
} from "../../../api/mosaic/interface";
import { handleGetPermissionsApi } from "../../../api/mosaic/roles";
import PermissionComponent from "./PermissionComponent";
import ModalWrapper, { modalCancelButtonSx, modalSubmitButtonSx } from "../../shared/ModalWrapper";
import { useToast } from "../../shared/Toast";
import theme from "../../../utils/theme";

interface IProps {
  openModal: boolean;
  handleClose: () => void;
  handleOnChange: (key: string, value: string) => void;
  handleOnUpdateRole: () => void;
  handleOnCreateRole: () => void;
  isNewForm: boolean;
  isLoading: boolean;
  formData: ICreateUpdateRole;
  setFormData: Dispatch<SetStateAction<ICreateUpdateRole>>;
}

const steps = ["Role Details", "Assign Permissions"];
const selectSx = { borderRadius: "8px" };

const RolesModal: React.FC<IProps> = ({
  openModal,
  handleClose,
  handleOnChange,
  handleOnUpdateRole,
  handleOnCreateRole,
  isNewForm,
  formData,
  setFormData,
  isLoading,
}) => {
  const companyData = useAppSelector((state) => state.companySlice.companyData);
  const [activeStep, setActiveStep] = React.useState(0);
  const [permissionData, setPermissionData] = React.useState<{ id: number; name: string }[]>([]);
  const { showError } = useToast();

  useEffect(() => {
    if (activeStep === 1) {
      const fetchPermissions = async () => {
        try {
          const permissions = await handleGetPermissionsApi();
          setPermissionData(permissions);
        } catch (err) {
          console.error(err);
        }
      };
      fetchPermissions();
    }
  }, [activeStep]);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
            <FormControl fullWidth>
              <NativeSelect
                value={formData.companyId}
                input={<OutlinedInput sx={selectSx} />}
                onChange={(e) => handleOnChange("companyId", e.target.value as string)}
              >
                <option value={0}>Select a company *</option>
                {companyData?.map((company) => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </NativeSelect>
            </FormControl>
            <TextField
              label="Role Name *"
              fullWidth
              value={formData.roleName}
              onChange={(e) => handleOnChange("roleName", e.target.value)}
              inputProps={{ maxLength: 100 }}
            />
          </Box>
        );
      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: "#878787", mb: 2 }}>
              Select permissions for {formData.roleName}
            </Typography>
            <PermissionComponent
              permissionData={permissionData}
              formData={formData}
              setFormData={setFormData}
              isNewForm={isNewForm}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <ModalWrapper
      open={openModal}
      onClose={handleClose}
      title={isNewForm ? "Create Role" : "Update Role"}
      actions={
        activeStep === 0 ? (
          <Button
            variant="contained"
            onClick={() => {
              if (formData.roleName.length > 0) {
                setActiveStep(1);
              } else {
                showError("Role name is required");
              }
            }}
            sx={modalSubmitButtonSx}
          >
            Next
          </Button>
        ) : (
          <>
            <Button variant="outlined" onClick={() => setActiveStep(0)} sx={modalCancelButtonSx}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={isNewForm ? handleOnCreateRole : handleOnUpdateRole}
              disabled={isLoading}
              sx={modalSubmitButtonSx}
            >
              {isNewForm ? "Create" : "Update"}
            </Button>
          </>
        )
      }
    >
      <Stepper
        activeStep={activeStep}
        sx={{
          mb: 2,
          "& .MuiStepIcon-root.Mui-active": { color: theme.palette.brand.primary },
          "& .MuiStepIcon-root.Mui-completed": { color: theme.palette.brand.success },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {renderStepContent()}
    </ModalWrapper>
  );
};

export default RolesModal;
